import React, { useEffect } from "react";
import { Redirect } from "next";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { parse } from "qs";

import getProductsV3 from "@/api/requests/consumerApi/getProductsV3";
import getRegionComplianceRules from "@/api/requests/consumerApi/getRegionComplianceRules";
import getShopCitiesNearMe from "@/api/requests/getShopCitiesNearMe";
import { getUserLocationData } from "@/api/requests/getUserLocation";
import serverConfig from "@/config/server";
import { City } from "@/custom-types/CitiesNearMe";
import { Query } from "@/custom-types/Query";
import { useDispatch, useSelector } from "@/redux/hooks";
import { getGeoSlug } from "@/redux/selectors/location";
import {
  ComplianceRuleset,
  loadComplianceRuleset,
} from "@/redux/slices/complianceRules";
import {
  disableLocationChangeReload,
  enableLocationChangeReload,
} from "@/redux/slices/header";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";
import { syncCookieLocationWithLocationParam } from "@/utils/getServerSidePropsLocationParamUtils";
import logError from "@/utils/logError";
import {
  LEGACY_FILTER_MAP,
  normalizeShopFilterParam,
} from "@/utils/normalizeShopFilterParam";
import {
  LEGACY_SORT_MAP,
  normalizeShopSortParam,
} from "@/utils/normalizeShopSortParam";
import { leaflyShopStringify } from "@/utils/qs";
import { getCountryCodeHeader } from "@/utils/requestContext";

import { AvailableFilter } from "@/components/DynamicFilters/types";
import { ShopItem } from "@/components/ShopItemCard/types";
import UnifiedShop, {
  UNIFIED_SHOP_PAGE_SIZE,
} from "@/components/UnifiedShop/UnifiedShop";

export type ShopProps = {
  shopItems: ShopItem[];
  availableFilters: AvailableFilter[];
  citiesNearby: City[];
  complianceRuleset: ComplianceRuleset | null;
  debugAPIQueryString?: string;
  shopAdsFlagEnabled?: boolean;
  totalCount: number;
};

const ShopPage: React.FC<ShopProps> = ({
  complianceRuleset,
  debugAPIQueryString,
  ...newProps
}) => {
  const { push } = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const dispatch = useDispatch();
  const reduxLocationSlug = useSelector(getGeoSlug);

  const { location: routeLocationSlug, ...otherQueryParams } = parse(
    searchParams?.toString() || "",
  );

  useEffect(() => {
    if (routeLocationSlug) {
      dispatch(disableLocationChangeReload());
    } else {
      dispatch(enableLocationChangeReload());
    }
  }, [routeLocationSlug]);

  useEffect(() => {
    if (reduxLocationSlug) {
      // * City / State was successfully resolved from Redux

      if (routeLocationSlug && routeLocationSlug !== reduxLocationSlug) {
        // * ...but the location query string param does not match
        const newQueryString = leaflyShopStringify({
          ...otherQueryParams,
        });
        push(`${pathname}${newQueryString ? `?${newQueryString}` : ""}`);
      }
    }
  }, [reduxLocationSlug, routeLocationSlug, otherQueryParams, pathname]);

  useEffect(() => {
    if (debugAPIQueryString) {
      /* eslint-disable no-console -- Useful for debugging /shop's backend API */
      console.log("Leafly internal dev logging:");
      console.log(`Products V3 API QS: ${debugAPIQueryString}`);
      /* eslint-enable no-console */
    }
  }, [debugAPIQueryString]);

  useEffect(() => {
    if (complianceRuleset) {
      dispatch(loadComplianceRuleset(complianceRuleset));
    }
  }, []);

  return <UnifiedShop {...newProps} />;
};

export const getServerSideProps = createGetServerSideProps<ShopProps>(
  async (context) => {
    const normalizedFiltersRedirect = getLegacyParamsRedirect(context);
    if (normalizedFiltersRedirect) {
      return { redirect: normalizedFiltersRedirect };
    }

    const countryCode = getCountryCodeHeader(context.req.headers);
    const userLocation = await getUserLocationData(countryCode, context);

    await syncCookieLocationWithLocationParam(context, userLocation);

    try {
      // * userLocation needs to be refreshed after syncCookieLocationWithLocationParam
      const userLocation = await getUserLocationData(countryCode, context);
      const [url, _hash] = context.resolvedUrl.split("#");
      const [_, query] = url.split("?");
      const { filter, fulfillment, q, sort, page } = parse(query);
      const currentPage = (parseInt(page as string) || 1) - 1;
      const { coordinates, state_code } = userLocation || {};

      const productHeaders = new Headers();
      if (context.req.headers["cookie"]) {
        productHeaders.append("cookie", context.req.headers["cookie"]);
      }

      const shopAdsFlagEnabled = serverConfig.tech1369ShopAdsFlag === "on";

      const apiParams: Parameters<typeof getProductsV3>[0] = {
        filter: filter as Query,
        fulfillment: fulfillment as string[],
        headers: productHeaders,
        lat: coordinates?.latitude || coordinates?.lat,
        lon: coordinates?.longitude || coordinates?.lon,
        personalizations: true,
        q: q as string,
        skip: currentPage * UNIFIED_SHOP_PAGE_SIZE,
        sort: sort as Query,
        take: UNIFIED_SHOP_PAGE_SIZE,
      };
      const [data, complianceRuleset, citiesNearby] = await Promise.all([
        getProductsV3(apiParams),
        getRegionComplianceRules(state_code, countryCode),
        getShopCitiesNearMe({
          countryCode: countryCode,
          location: userLocation,
          retailType: "dispensary",
        }),
      ]);

      const allowedEnvironments = ["staging", "integration", "local-dev"];
      const includeDebugInformation = allowedEnvironments.includes(
        serverConfig.env,
      );

      return {
        props: {
          availableFilters: data?.metadata.availableFilters || [],
          citiesNearby: citiesNearby,
          complianceRuleset: complianceRuleset,
          debugAPIQueryString: includeDebugInformation
            ? leaflyShopStringify(apiParams)
            : undefined,
          disableFixedHeader: true,
          shopAdsFlagEnabled: shopAdsFlagEnabled,
          shopItems: data?.data || [],
          totalCount: data?.metadata.totalCount || 0,
        },
      };
    } catch (error) {
      logError(error.toString(), {
        functionName: "/shop getServerSideProps",
      });
      return {
        props: {
          availableFilters: [],
          citiesNearby: [],
          complianceRuleset: null,
          disableFixedHeader: true,
          shopAdsFlagEnabled: false,
          shopItems: [],
          totalCount: 0,
        },
      };
    }
  },
);

const getLegacyParamsRedirect = (
  context: Parameters<
    ReturnType<typeof createGetServerSideProps<ShopProps>>
  >[0],
): Redirect | undefined => {
  const [url, _hash] = context.resolvedUrl.split("#");
  const [path, query] = url.split("?");
  const { filter, fulfillmentPreference, sort, ...others } = parse(query);

  const shouldUpdateSort =
    sort &&
    Object.keys(sort).some((appliedSort) =>
      Object.keys(LEGACY_SORT_MAP).includes(appliedSort),
    );

  const shouldUpdateFilters =
    filter &&
    Object.keys(filter).some((appliedFilter) =>
      Object.keys(LEGACY_FILTER_MAP).includes(appliedFilter),
    );
  // * Handle legacy filters & fulfillment param by redirecting update filter keys, if detected.
  if (shouldUpdateFilters || fulfillmentPreference || shouldUpdateSort) {
    return {
      destination: `${path}?${leaflyShopStringify({
        filter: normalizeShopFilterParam(filter as Query),
        fulfillment: [fulfillmentPreference],
        sort: normalizeShopSortParam(sort as Query),
        ...others,
      })}`,
      statusCode: 302,
    };
  }
};

export default ShopPage;
